import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/compat/router';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';

import { BaseButton } from 'src/components/kiosk-pro/kiosk-pro-layout/consumer-info/consumer-info.styles';
import { ModalContainer } from 'shared/modals';

const TUTORIAL_STEPS = [
  ['1', 'Browse the menu', 'Use categories, search, and filters to find products.'],
  ['2', 'Build your cart', 'Tap a product to review details and add it to your order.'],
  ['3', 'Check out', 'Review your cart and follow the prompts to finish your order.'],
] as const;

export function KioskProTutorialModal({
  isOpen,
  onClose,
  isLockScreen,
}: {
  isOpen: boolean;
  onClose: () => void;
  isLockScreen?: boolean;
}): JSX.Element {
  const router = useRouter();
  const isOnLockScreen = isLockScreen ?? Boolean(router?.pathname.includes('/lock'));

  const handleClose = (): void => {
    if (isOnLockScreen) {
      try {
        clearAllBodyScrollLocks();
      } catch {
        // best-effort scroll lock release
      }
    }
    onClose();
  };

  useEffect(
    () => () => {
      if (isOnLockScreen) {
        try {
          clearAllBodyScrollLocks();
        } catch {
          // best-effort scroll lock release
        }
      }
    },
    [isOnLockScreen]
  );

  return (
    <TutorialContainer
      isOpen={isOpen}
      onRequestClose={handleClose}
      {...(!isOnLockScreen ? { onAfterOpen: () => undefined } : {})}
      width='820px'
      padding='48px'
      borderRadius='24px'
      ariaHideApp={false}
      contentLabel='How Kiosk Pro works'
      data-testid='kiosk-pro-tutorial-modal'
    >
      <h2>How it works</h2>
      <p>Ordering takes just a few steps.</p>
      <StepList>
        {TUTORIAL_STEPS.map(([number, title, description]) => (
          <li key={number}>
            <StepNumber aria-hidden='true'>{number}</StepNumber>
            <div>
              <h3>{title}</h3>
              <p>{description}</p>
            </div>
          </li>
        ))}
      </StepList>
      <DoneButton onClick={handleClose} autoFocus>
        Start shopping
      </DoneButton>
    </TutorialContainer>
  );
}

const TutorialContainer = styled(ModalContainer)`
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;

  > h2 {
    font-size: 40px;
    margin-bottom: 8px;
  }

  > p {
    font-size: 22px;
  }
`;

const StepList = styled.ol`
  list-style: none;
  display: grid;
  gap: 20px;
  margin: 36px 0;
  text-align: left;

  li {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 20px;
    border-radius: 16px;
    background: ${({ theme }) => theme.colors.grey[95]};
  }

  h3 {
    font-size: 24px;
    margin-bottom: 4px;
  }

  p {
    font-size: 18px;
  }
`;

const StepNumber = styled.span`
  display: grid;
  place-items: center;
  flex: 0 0 52px;
  height: 52px;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.customized.colors.buttonsLinks};
  font-size: 24px;
  font-weight: 700;
`;

const DoneButton = styled(BaseButton)`
  max-width: 360px;
`;
